/**
 * Progress Router Hooks
 *
 * Custom hooks for the progress router endpoints.
 * These provide typed access to progress data while the progress router
 * types are not yet included in the @ds/ds-api-types package.
 *
 * Backend source: packages/serverless/api/src/handlers/monumenta-trpc/progress/router.ts
 */

import { useQuery } from '@tanstack/react-query'
import { get, post } from 'aws-amplify/api'
import type {
  GetGlobalProgressInput,
  GetGlobalProgressOutput,
  GetMyClientProgressInput,
  GetMyClientProgressOutput,
  GetClientProgressInput,
  GetClientProgressOutput,
} from './progress-router.types'

/**
 * Custom fetch function that uses Amplify's API client with automatic IAM signing
 * (Duplicated from trpc.ts to avoid circular imports)
 */
async function customFetch(path: string, input: unknown): Promise<unknown> {
  const apiOperation = post({
    apiName: 'monumenta-trpc',
    path,
    options: {
      body: input as Record<string, unknown>,
    } as Parameters<typeof post>[0]['options'],
  })

  const response = await apiOperation.response
  const body = await response.body.text()
  const parsed = JSON.parse(body)

  // tRPC wraps results in a { result: { data: ... } } structure
  if (parsed.result?.data) {
    return parsed.result.data
  }
  if (parsed.error) {
    throw new Error(parsed.error.message || 'Unknown error')
  }
  return parsed
}

/**
 * Hook for fetching global progress across all clients
 * Admin-only endpoint showing company-wide PROA achievement
 */
export function useGlobalProgress(
  input: GetGlobalProgressInput,
  options?: { enabled?: boolean }
) {
  return useQuery<GetGlobalProgressOutput>({
    queryKey: ['progress', 'getGlobalProgress', input],
    queryFn: async () => {
      const result = await customFetch('/progress/getGlobalProgress', input)
      return result as GetGlobalProgressOutput
    },
    enabled: options?.enabled ?? true,
  })
}

/**
 * Hook for fetching progress for the current user's assigned clients
 * Returns only clients the user is authorized to see
 */
export function useMyClientProgress(
  input: GetMyClientProgressInput,
  options?: { enabled?: boolean }
) {
  return useQuery<GetMyClientProgressOutput>({
    queryKey: ['progress', 'getMyClientProgress', input],
    queryFn: async () => {
      const result = await customFetch('/progress/getMyClientProgress', input)
      return result as GetMyClientProgressOutput
    },
    enabled: options?.enabled ?? true,
  })
}

/**
 * Hook for fetching progress for specific clients (with authorization check)
 * User must have access to all requested clients
 */
export function useClientProgress(
  input: GetClientProgressInput,
  options?: { enabled?: boolean }
) {
  return useQuery<GetClientProgressOutput>({
    queryKey: ['progress', 'getClientProgress', input],
    queryFn: async () => {
      const result = await customFetch('/progress/getClientProgress', input)
      return result as GetClientProgressOutput
    },
    enabled: options?.enabled ?? true,
  })
}
